import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from '@rbx/intl';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Typography,
} from '@rbx/ui';

interface TryItOutHighRiskWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
}

/**
 * Warning dialog shown once per session before executing HIGH risk API operations.
 * This appears when the user clicks Execute on a high-risk endpoint.
 */
const TryItOutHighRiskWarningDialog: FunctionComponent<TryItOutHighRiskWarningDialogProps> = ({
  open,
  onClose,
  onContinue,
}) => {
  const { translate } = useTranslation();
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAcknowledged(event.target.checked);
  };

  const handleClose = () => {
    setIsAcknowledged(false);
    onClose();
  };

  const handleContinue = () => {
    setIsAcknowledged(false);
    onContinue();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='Large' fullWidth>
      <DialogTitle>{translate('Heading.TryItOutHighRiskWarning')}</DialogTitle>
      <DialogContent>
        <Alert severity='warning' style={{ marginBottom: '16px' }}>
          {translate('Description.TryItOutHighRiskWarningAlert')}
        </Alert>

        <Typography variant='body1' style={{ marginBottom: '16px' }}>
          {translate('Description.TryItOutHighRiskWarningBody')}
        </Typography>

        <Divider style={{ margin: '16px 0' }} />

        <FormControlLabel
          control={
            <Checkbox checked={isAcknowledged} onChange={handleCheckboxChange} color='primary' />
          }
          label={translate('Label.TryItOutHighRiskWarningAcknowledge')}
        />
      </DialogContent>
      <DialogActions style={{ paddingTop: '0px' }}>
        <Button onClick={handleClose} variant='outlined'>
          {translate('Action.TryItOutHighRiskWarningCancel')}
        </Button>
        <Button
          onClick={handleContinue}
          variant='contained'
          color='primaryBrand'
          disabled={!isAcknowledged}>
          {translate('Action.TryItOutHighRiskWarningContinue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TryItOutHighRiskWarningDialog;
